import React, { useState, useEffect } from 'react';
import { twoFactorAPI } from '../services/api';
import { formatApiError } from '../utils/errorMessage';

/**
 * Two-Factor Authentication management (Settings page)
 * Single Responsibility: enroll/disable TOTP and email-based 2FA, show recovery codes
 */
const TwoFactorSettings = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // TOTP enrollment flow
  const [totpSetupData, setTotpSetupData] = useState(null); // { qrCode, secret }
  const [totpConfirmCode, setTotpConfirmCode] = useState('');
  const [totpDisablePassword, setTotpDisablePassword] = useState('');
  const [showTotpDisable, setShowTotpDisable] = useState(false);

  // Email 2FA enrollment flow
  const [emailSetupSent, setEmailSetupSent] = useState(false);
  const [emailConfirmCode, setEmailConfirmCode] = useState('');
  const [emailDisablePassword, setEmailDisablePassword] = useState('');
  const [showEmailDisable, setShowEmailDisable] = useState(false);

  // Recovery codes
  const [recoveryCodes, setRecoveryCodes] = useState(null); // shown once after (re)generation
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [showRegenerate, setShowRegenerate] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await twoFactorAPI.getStatus();
      setStatus(res.data);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load two-factor authentication status'));
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const withLoading = async (fn) => {
    setError('');
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      setError(formatApiError(err, 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  const startTotpSetup = () => withLoading(async () => {
    const res = await twoFactorAPI.totpSetup();
    setTotpSetupData(res.data);
  });

  const confirmTotpSetup = () => withLoading(async () => {
    const res = await twoFactorAPI.totpConfirm(totpConfirmCode);
    setTotpSetupData(null);
    setTotpConfirmCode('');
    setRecoveryCodes(res.data.recoveryCodes);
    await loadStatus();
  });

  const disableTotp = () => withLoading(async () => {
    await twoFactorAPI.totpDisable(totpDisablePassword);
    setShowTotpDisable(false);
    setTotpDisablePassword('');
    await loadStatus();
  });

  const startEmailSetup = () => withLoading(async () => {
    await twoFactorAPI.emailSetup();
    setEmailSetupSent(true);
  });

  const confirmEmailSetup = () => withLoading(async () => {
    await twoFactorAPI.emailConfirm(emailConfirmCode);
    setEmailSetupSent(false);
    setEmailConfirmCode('');
    await loadStatus();
  });

  const disableEmail = () => withLoading(async () => {
    await twoFactorAPI.emailDisable(emailDisablePassword);
    setShowEmailDisable(false);
    setEmailDisablePassword('');
    await loadStatus();
  });

  const regenerateRecoveryCodes = () => withLoading(async () => {
    const res = await twoFactorAPI.regenerateRecoveryCodes(regeneratePassword);
    setRecoveryCodes(res.data.recoveryCodes);
    setShowRegenerate(false);
    setRegeneratePassword('');
  });

  if (!status) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Two-Factor Authentication</h2>
        {error ? <p className="text-red-600 text-sm" role="alert">{error}</p> : <p className="text-gray-500 text-sm">Loading...</p>}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-8">
      <div>
        <h2 className="text-xl font-semibold">Two-Factor Authentication</h2>
        <p className="text-gray-600 text-sm mt-1">
          Add a second step when signing in, so a leaked password alone can't get into your account.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm" role="alert">{error}</div>
      )}

      {recoveryCodes && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-yellow-900">Save your recovery codes</h3>
          <p className="text-sm text-yellow-800">
            Each code can be used once to sign in (or reset your password) if you lose access to your
            authenticator app or email. Store these somewhere safe - they won't be shown again.
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-white border border-yellow-200 rounded p-3">
            {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
          <button
            onClick={() => setRecoveryCodes(null)}
            className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
          >
            I've saved these
          </button>
        </div>
      )}

      {/* Authenticator app (TOTP) */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Authenticator App</h3>
            <p className="text-sm text-gray-500">Google Authenticator, Authy, 1Password, etc.</p>
          </div>
          {status.totp_enabled ? (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Enabled</span>
          ) : (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">Disabled</span>
          )}
        </div>

        {status.totp_enabled ? (
          showTotpDisable ? (
            <div className="mt-3 space-y-2 max-w-sm">
              <label className="block text-sm font-medium text-gray-700">Confirm your password to disable</label>
              <input
                type="password"
                value={totpDisablePassword}
                onChange={(e) => setTotpDisablePassword(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
              <div className="flex gap-2">
                <button onClick={disableTotp} disabled={loading} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:bg-red-300">
                  Disable
                </button>
                <button onClick={() => { setShowTotpDisable(false); setTotpDisablePassword(''); }} className="px-3 py-1.5 border border-gray-300 text-sm rounded hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowTotpDisable(true)} className="mt-3 text-sm text-red-600 hover:underline">
              Disable
            </button>
          )
        ) : totpSetupData ? (
          <div className="mt-3 space-y-3 max-w-sm">
            <p className="text-sm text-gray-600">Scan this QR code with your authenticator app:</p>
            <img src={totpSetupData.qrCode} alt="TOTP QR code" className="border rounded" width={200} height={200} />
            <p className="text-xs text-gray-500">
              Can't scan it? Enter this code manually: <span className="font-mono">{totpSetupData.secret}</span>
            </p>
            <label className="block text-sm font-medium text-gray-700">Enter the 6-digit code from your app</label>
            <input
              type="text"
              value={totpConfirmCode}
              onChange={(e) => setTotpConfirmCode(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="123456"
            />
            <div className="flex gap-2">
              <button onClick={confirmTotpSetup} disabled={loading} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-blue-300">
                Confirm
              </button>
              <button onClick={() => { setTotpSetupData(null); setTotpConfirmCode(''); }} className="px-3 py-1.5 border border-gray-300 text-sm rounded hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button onClick={startTotpSetup} disabled={loading} className="mt-3 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-blue-300">
            Enable
          </button>
        )}
      </div>

      {/* Email codes */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Email Verification</h3>
            <p className="text-sm text-gray-500">Get a code by email when signing in.</p>
          </div>
          {status.email_enabled ? (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Enabled</span>
          ) : (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">Disabled</span>
          )}
        </div>

        {status.email_enabled ? (
          showEmailDisable ? (
            <div className="mt-3 space-y-2 max-w-sm">
              <label className="block text-sm font-medium text-gray-700">Confirm your password to disable</label>
              <input
                type="password"
                value={emailDisablePassword}
                onChange={(e) => setEmailDisablePassword(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
              <div className="flex gap-2">
                <button onClick={disableEmail} disabled={loading} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:bg-red-300">
                  Disable
                </button>
                <button onClick={() => { setShowEmailDisable(false); setEmailDisablePassword(''); }} className="px-3 py-1.5 border border-gray-300 text-sm rounded hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowEmailDisable(true)} className="mt-3 text-sm text-red-600 hover:underline">
              Disable
            </button>
          )
        ) : emailSetupSent ? (
          <div className="mt-3 space-y-2 max-w-sm">
            <p className="text-sm text-gray-600">We emailed you a verification code.</p>
            <label className="block text-sm font-medium text-gray-700">Enter the code</label>
            <input
              type="text"
              value={emailConfirmCode}
              onChange={(e) => setEmailConfirmCode(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="123456"
            />
            <div className="flex gap-2">
              <button onClick={confirmEmailSetup} disabled={loading} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-blue-300">
                Confirm
              </button>
              <button onClick={() => { setEmailSetupSent(false); setEmailConfirmCode(''); }} className="px-3 py-1.5 border border-gray-300 text-sm rounded hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button onClick={startEmailSetup} disabled={loading} className="mt-3 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-blue-300">
            Enable
          </button>
        )}
      </div>

      {/* Recovery codes */}
      {(status.totp_enabled || status.email_enabled) && (
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900">Recovery Codes</h3>
          <p className="text-sm text-gray-500 mt-1">
            Generate a fresh set of one-time recovery codes. Doing this invalidates any codes you were
            given before.
          </p>
          {showRegenerate ? (
            <div className="mt-3 space-y-2 max-w-sm">
              <label className="block text-sm font-medium text-gray-700">Confirm your password</label>
              <input
                type="password"
                value={regeneratePassword}
                onChange={(e) => setRegeneratePassword(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
              <div className="flex gap-2">
                <button onClick={regenerateRecoveryCodes} disabled={loading} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-blue-300">
                  Generate New Codes
                </button>
                <button onClick={() => { setShowRegenerate(false); setRegeneratePassword(''); }} className="px-3 py-1.5 border border-gray-300 text-sm rounded hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowRegenerate(true)} className="mt-3 text-sm text-blue-600 hover:underline">
              Regenerate recovery codes
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
